import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useState } from 'react';
// @mui
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Tooltip from '@mui/material/Tooltip';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { _roles, _userList, USER_STATUS_OPTIONS } from 'src/_mock';
import { useBoolean } from 'src/hooks/use-boolean';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { ConfirmDialog } from 'src/components/custom-dialog';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import {
    emptyRows,
    getComparator,
    TableEmptyRows,
    TableHeadCustom,
    TableNoData,
    TablePaginationCustom,
    TableSelectedAction,
    useTable,
} from 'src/components/table';
import UserTableFiltersResult from '../../support-tickets/supportTicket-table-filter';
import UserTableRow from './list-table-row';
import UserTableToolbar from './list-table-show-toolebar';
import { getRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { applyFilter } from 'src/services/fetching';
import { LoadingScreen } from 'src/components/loading-screen';
import { Box } from '@mui/material';
// ----------------------------------------------------------------------

const TABLE_HEAD = [
    { id: 'name', label: 'ID' },
    { id: 'phoneNumber', label: 'Card Name' },
    { id: 'company', label: 'Summary' },
    { id: 'status', label: 'Status' },
    { id: 'role', label: 'Assignee' },
    { id: '', label: 'Due Date' },
    { id: '', label: 'Labels' },
    { id: '', label: 'Created At' },
    { id: '', label: 'Updated At' },
    { id: '', label: 'Reporter' },
];
const defaultFilters = {
    name: '',
    role: [],
    status: 'all',
};
// ----------------------------------------------------------------------
export default function MarketigListTable() {
    const table = useTable();
    const router = useRouter();
    const add = useBoolean();
    const confirm = useBoolean();

    const [ tableData, setTableData ] = useState(_userList);
    const [ selectedItem, setSelectedItem ] = useState({})
    const [ loading, setLoading ] = useState(false)
    const [ filters, setFilters ] = useState(defaultFilters);

    useEffect(() => {
        getMarketingList();
    }, []);
    const getMarketingList = async () => {
        setLoading(true)
        const data = await getRequest(`${endpoints.admin_dashboard.marketing_management.timeline.get_cards}`);
        if (data) {
            setTableData(data);
        }
        setLoading(false)
    };

    const dataFiltered = applyFilter({
        inputData: tableData,
        comparator: getComparator(table.order, table.orderBy),
        filters,
    });

    const dataInPage = dataFiltered.slice(
        table.page * table.rowsPerPage,
        table.page * table.rowsPerPage + table.rowsPerPage
    );
    const denseHeight = table.dense ? 52 : 72;
    const canReset = !isEqual(defaultFilters, filters);
    const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;
    const handleFilters = useCallback(
        (name, value) => {
            table.onResetPage();
            setFilters((prevState) => ({
                ...prevState,
                [ name ]: value,
            }));
        },
        [ table ]
    );

    const handleDeleteRow = useCallback(
        (id) => {
            const deleteRow = tableData.filter((row) => row.id !== id);
            setTableData(deleteRow);
            table.onUpdatePageDeleteRow(dataInPage.length);
        },
        [ dataInPage.length, table, tableData ]
    );

    const handleDeleteRows = useCallback(() => {
        const deleteRows = tableData.filter((row) => !table.selected.includes(row.id));
        setTableData(deleteRows);
        table.onUpdatePageDeleteRows({
            totalRows: tableData.length,
            totalRowsInPage: dataInPage.length,
            totalRowsFiltered: dataFiltered.length,
        });
    }, [ dataFiltered.length, dataInPage.length, table, tableData ]);

    const handleEditRow = useCallback(

        [ router ]
    );

    const handleFilterStatus = useCallback(
        (event, newValue) => {
            handleFilters('status', newValue);
        },
        [ handleFilters ]
    );

    const handleResetFilters = useCallback(() => {
        setFilters(defaultFilters);
    }, []);

    return (
        <>
            <CustomBreadcrumbs
                heading="List"
                links={ [
                    { name: '', href: paths.dashboard.root },
                ] }
            />
            { loading ? (
                <Box sx={ { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' } }>
                    <LoadingScreen />
                </Box>
            ) : (
                <Card >
                    < UserTableToolbar
                        filters={ filters }
                        onFilters={ handleFilters }
                        roleOptions={ _roles }
                    />
                    { canReset && (
                        <UserTableFiltersResult
                            filters={ filters }
                            onFilters={ handleFilters }
                            //
                            onResetFilters={ handleResetFilters }
                            //
                            results={ dataFiltered.length }
                            sx={ { p: 2.5, pt: 0 } }
                        />
                    ) }

                    <TableContainer sx={ { position: 'relative', overflow: 'unset' } }>
                        <TableSelectedAction
                            dense={ table.dense }
                            numSelected={ table.selected.length }
                            rowCount={ tableData.length }
                            onSelectAllRows={ (checked) =>
                                table.onSelectAllRows(
                                    checked,
                                    tableData.map((row) => row.id)
                                )
                            }
                            action={
                                <Tooltip title="Delete">
                                    <IconButton color="primary" onClick={ confirm.onTrue }>
                                        <Iconify icon="solar:trash-bin-trash-bold" />
                                    </IconButton>
                                </Tooltip>
                            }
                        />
                        <Scrollbar>
                            <Table size={ table.dense ? 'small' : 'medium' } sx={ { minWidth: 960 } }>
                                <TableHeadCustom
                                    order={ table.order }
                                    orderBy={ table.orderBy }
                                    headLabel={ TABLE_HEAD }
                                    rowCount={ tableData.length }
                                    numSelected={ table.selected.length }
                                    onSort={ table.onSort }
                                    onSelectAllRows={ (checked) =>
                                        table.onSelectAllRows(
                                            checked,
                                            tableData.map((row) => row.id)
                                        )
                                    }
                                />
                                <TableBody>
                                    { dataFiltered
                                        .slice(
                                            table.page * table.rowsPerPage,
                                            table.page * table.rowsPerPage + table.rowsPerPage
                                        )
                                        .map((row, index) => (
                                            <UserTableRow
                                                key={ row.id }
                                                row={ row }
                                                selected={ table.selected.includes(row.id) }
                                                onSelectRow={ () => table.onSelectRow(row.id) }
                                                onDeleteRow={ () => handleDeleteRow(row.id) }
                                                onEditRow={ () => handleEditRow(row.id) }
                                                index={ table.page * table.rowsPerPage + index + 1 }
                                            />
                                        )) }

                                    <TableEmptyRows
                                        height={ denseHeight }
                                        emptyRows={ emptyRows(table.page, table.rowsPerPage, tableData.length) }
                                    />
                                    <TableNoData notFound={ notFound } />
                                </TableBody>
                            </Table>
                        </Scrollbar>
                    </TableContainer>
                    <TablePaginationCustom
                        count={ dataFiltered.length }
                        page={ table.page }
                        rowsPerPage={ table.rowsPerPage }
                        onPageChange={ table.onChangePage }
                        onRowsPerPageChange={ table.onChangeRowsPerPage }
                        dense={ table.dense }
                        onChangeDense={ table.onChangeDense }
                    />
                </Card >
            ) }
            <ConfirmDialog
                open={ confirm.value }
                onClose={ confirm.onFalse }
                title="Delete"
                content={
                    <>
                        Are you sure want to delete <strong> { table.selected.length } </strong> items?
                    </>
                }
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={ () => {
                            handleDeleteRows();
                            confirm.onFalse();
                        } }
                    >
                        Delete
                    </Button>
                }
            />
        </>
    );
}